import React from "react";
import psl from "psl";
import isEmail from "validator/lib/isEmail";
import { Backdrop } from "@material-ui/core";
import Cookies from "js-cookie";
import { GoogleAuthProvider } from "firebase/auth";

import getLogger, { LogGroup } from "js/core/logger";
import Api from "js/core/api";
import { email as emailApi } from "apis-v2/callables";
import Analytics from "js/analytics";
import { CreateAccount, ResetPassword, SignIn, ResetDone, WaitForGoogle, WaitForMicrosoft, VerifyEmail } from "./AuthUi";
import { AuthConsumer } from "./AuthConsumer";
import { FirebaseSessionTracker } from "./FirebaseSessionTracker";
import { Experiment } from "../../components/Experiment";
import { FirebaseUserConsumer } from "./FirebaseUserContext";
import { RemoveSplashScreen, RestoreSplashScreen } from "js/core/SplashScreen";
import * as googleAuth from "js/core/oauth/googleAuth";
import { getGoogleAuthTokens } from "js/core/oauth/getGoogleAuthTokens";
import * as microsoftAuth from "js/core/oauth/microsoftAuth";
import { initializeAllExperiments } from "js/core/services/experiments";
import Spinner from "js/react/components/Spinner";
import { browserHistory } from "js/react/history";
import { removeQueryParam, addQueryParam } from "js/core/utilities/queryParams";

const logger = getLogger(LogGroup.AUTH);

class GoogleRedirectHandler extends React.Component {
    async componentDidMount() {
        const { signInWithGoogleIdToken, onError } = this.props;

        const params = new URLSearchParams(window.location.search);
        if (params.has("google_auth_success") && params.get("google_auth_success") === "true") {
            // token_key is no longer passed in URL, tokens are retrieved from localStorage
            const { idToken } = getGoogleAuthTokens();

            if (!idToken) {
                // Cookie not available (can be caused by Safari private mode blocking cookies)
                logger.error("[GoogleRedirectHandler] Google auth id token not found in localStorage");
                onError("Authentication failed: Unable to retrieve authentication token. Safari privacy protection may be in effect. Please try again or use email/password sign-in.");
                return;
            }

            // Signing in
            signInWithGoogleIdToken(idToken);
            return;
        }

        if (params.has("google_auth_error")) {
            // Cleanup
            removeQueryParam("google_auth_error");

            onError(params.get("google_auth_error"));
        }
    }

    render() {
        const { children } = this.props;
        return children;
    }
}

class MicrosoftRedirectHandler extends React.Component {
    componentDidMount() {
        const { signInWithMicrosoftIdToken, onError } = this.props;

        const params = new URLSearchParams(window.location.search);
        if (params.has("microsoft_auth_success") && params.get("microsoft_auth_success") === "true") {
            const customToken = localStorage.getItem("microsoft_auth_custom_token");
            if (customToken) {
                localStorage.removeItem("microsoft_auth_custom_token");
            }

            if (!customToken) {
                logger.error("[MicrosoftRedirectHandler] Microsoft auth custom token not found in localStorage");
                onError("Authentication failed: Unable to retrieve authentication token. Safari privacy protection may be in effect. Please try again or use email/password sign-in.");
                return;
            }

            // Signing in
            signInWithMicrosoftIdToken(customToken);
            return;
        }

        if (params.has("microsoft_auth_error")) {
            // Cleanup
            removeQueryParam("microsoft_auth_error");

            onError(params.get("microsoft_auth_error"));
        }
    }

    render() {
        const { children } = this.props;
        return children;
    }
}

export function Authenticate(props) {
    return (
        <AuthConsumer>
            {auth => (
                <FirebaseUserConsumer>
                    {currentUser => (
                        <AuthenticateImplementation {...props} auth={auth} currentUser={currentUser} />
                    )}
                </FirebaseUserConsumer>
            )}
        </AuthConsumer>
    );
}

class AuthenticateImplementation extends React.Component {
    constructor(props) {
        super(props);

        if (props.defaultPage != "signIn" && props.defaultPage != "createAccount") {
            throw new Error(
                "<Authenticate> `defaultPage` prop is required " +
                "(must be `signIn` or `createAccount`)"
            );
        }

        let error;
        let info;
        if (props.readonlyEmail) {
            if (props.presentationPermission === "private") {
                error = "This presentation is private.";
            }
        }

        this._isUnmounted = false;

        this.state = {
            error,
            info,
            loading: false,
            isReady: false,
            page: props.defaultPage || "signIn",
            email: props.readonlyEmail || "",
            password: "",
            ssoState: "none"
        };
    }

    componentDidMount = async () => {
        const { readonlyEmail } = this.props;

        // When the email is readonly, we need to check for SSO up front
        if (readonlyEmail) {
            this.checkEmail(readonlyEmail);
        }

        // Track the zoom app flag in analytics, so auth events can be tagged with it
        if (this.props.reason === "zoom-app") {
            Analytics.util.trackState({ isZoomApp: true });
        }

        this.setState({ isReady: true });
    }

    componentDidUpdate = (prevProps, prevState) => {
        if (prevProps.currentUser !== this.props.currentUser && !this.props.currentUser) {
            // Logged out
            this.setState({ page: "signIn" });
        }
    }

    componentWillUnmount = () => {
        this._isUnmounted = true;
    }

    handleSignIn = auth => (async () => {
        const { email, password } = this.state;
        try {
            this.setState({ loading: true, error: undefined, emailError: undefined, passwordError: undefined });

            if (email == "") {
                this.setState({ loading: false, emailError: "Required" });
                return;
            }

            // clicking sign-in an email but no password begins SSO (got this idea from Dropbox)
            if (email) {
                try {
                    const ssoState = this.state.ssoState;
                    if (ssoState === "strict" || (ssoState === "optional" && password === "")) {
                        window.location = `/api/sso/saml/login?email=${encodeURIComponent(email)}`;
                        return;
                    }
                } catch (err) {
                    this.setState({ loading: false, error: err.message });
                    return;
                }
            }

            const userCredential = await auth.signInWithEmailAndPassword(email, password);

            // Ensure all experiments are initialized for the user
            await initializeAllExperiments().catch(err => logger.error(err, "Failed to initialize experiments"));

            // resume the loading animation, if any
            RestoreSplashScreen();

            const isNew = await this.ensureRegistered("email");
            if (isNew) {
                this.trackCreateAccount(userCredential.user, false);
            } else {
                this.trackLogin(userCredential.user);
            }

            if (this._isUnmounted) {
                return;
            }

            this.setState({ loading: false });
        } catch (err) {
            this.trackError(email, err);
            if (err.code == "auth/invalid-email") {
                this.setState({ loading: false, emailError: "Invalid email" });
            } else if (err.code == "auth/user-not-found") {
                this.setState({ loading: false, emailError: "No account for this email" });
            } else if (err.code == "auth/wrong-password") {
                this.setState({ loading: false, passwordError: password == "" ? "Required" : "Incorrect password" });
            } else {
                this.setState({ loading: false, error: err.message });
            }
        }
    })

    // note: for auth with Google, logging in and creating an account are the same thing
    handleGoogle = auth => (async () => {
        const { popup } = this.props;
        const { page: startPage } = this.state;

        this.setState({ page: "waitForGoogle", loading: true, error: undefined, isSigningInToGoogle: true });

        const scope = "profile email";

        if (!popup) {
            // Do proper redirect flow
            googleAuth.authenticate(scope, `${window.location.pathname}${window.location.search}`);
            return;
        }

        // Do fake popup (redirect flow in a child window)
        try {
            const { idToken } = await googleAuth.authenticateWithPopup(scope);
            this.handleGoogleIdToken(auth, idToken);
        } catch (err) {
            const error = err instanceof googleAuth.GoogleAuthFlowWasInterruptedError ? null : err.message;
            this.setState({ page: startPage, loading: false, error, isSigningInToGoogle: false });
        }
    })

    handleGoogleIdToken = async (auth, idToken) => {
        const { page: startPage } = this.state;

        this.setState({ page: "waitForGoogle", loading: true, error: undefined, isSigningInToGoogle: true });

        try {
            await this.signInWithGoogleIdToken(auth, idToken);
        } catch (err) {
            this.setState({ page: startPage, loading: false, error: err.message, isSigningInToGoogle: false });
        }
    }

    ensureRegistered = async provider => {
        const { currentUser } = this.props;

        logger.info("Calling registerUser()");

        const relativeUrl = window.location.href.substr(window.location.origin.length);
        const rawPsXid = Cookies.get("ps_xid");
        const rawPsPartnerKey = Cookies.get("ps_partner_key");

        const partnerStackClickId = typeof rawPsXid === "string" ? rawPsXid.substring(0, 255) : undefined;
        const partnerStackPartnerKey = typeof rawPsPartnerKey === "string" ? rawPsPartnerKey.substring(0, 255) : undefined;

        const isNew = await Api.registerUser.post({
            affiliate: this.props.affiliate,
            provider,
            referer: relativeUrl,
            partnerStackClickId,
            partnerStackPartnerKey,
        });

        // Mount the flag on the current user so downstream components can know if the user
        // was registered in the current session
        currentUser.signedUpInCurrentSession = isNew;

        logger.info(`User registered ${isNew ? "new" : "existing"}`);

        return isNew;
    }

    signInWithGoogleIdToken = async (auth, idToken) => {
        logger.info("Signing in to Firebase with Google auth credential");
        const credential = GoogleAuthProvider.credential(idToken);
        const userCredential = await auth.signInWithCredential(credential);

        // resume the loading animation, if any
        RestoreSplashScreen();

        // Ensure all experiments are initialized for the user
        await initializeAllExperiments().catch(err => logger.error(err, "Failed to initialize experiments"));

        // first sign-in results in account creation
        const isNew = await this.ensureRegistered("google");
        if (isNew) {
            // Letting the consumers know that we're signing up a new user
            // we don't need to do that for email/password sign up because
            // it'll be set by the server upon email verification
            addQueryParam("signup", "true");

            this.trackCreateAccount(userCredential.user, true);
        } else {
            this.trackLogin(userCredential.user);
        }

        if (this._isUnmounted) {
            return;
        }

        this.setState({ loading: false, isSigningInToGoogle: false });
    }

    // note: for auth with Microsoft, logging in and creating an account are the same thing
    handleMicrosoft = auth => (async () => {
        const { popup } = this.props;
        const { page: startPage } = this.state;

        this.setState({ page: "waitForMicrosoft", loading: true, error: undefined, isSigningInToMicrosoft: true });

        if (!popup) {
            // Do proper redirect flow
            microsoftAuth.authenticate(microsoftAuth.AUTH_SCOPES.join(" "), `${window.location.pathname}${window.location.search}`);
            return;
        }

        // Do fake popup (redirect flow in a child window)
        try {
            const { customToken } = await microsoftAuth.authenticateWithPopup(microsoftAuth.AUTH_SCOPES.join(" "));
            this.handleMicrosoftIdToken(auth, customToken);
        } catch (err) {
            const error = err instanceof microsoftAuth.MicrosoftAuthFlowWasInterruptedError ? null : err.message;
            this.setState({ page: startPage, loading: false, error, isSigningInToMicrosoft: false });
        }
    })

    handleMicrosoftError = error => {
        const existingUserUpdated = localStorage.getItem("microsoft_auth_existing_user_updated") === "true";

        if (localStorage.getItem("microsoft_auth_existing_user_updated")) {
            localStorage.removeItem("microsoft_auth_existing_user_updated");
        }

        if (existingUserUpdated) {
            browserHistory.push(`/account/me?microsoft_auth_error=${error}`);
        } else {
            this.setState({ error });
        }
    }

    handleMicrosoftIdToken = async (auth, customToken) => {
        const { page: startPage } = this.state;

        this.setState({ page: "waitForMicrosoft", loading: true, error: undefined, isSigningInToMicrosoft: true });

        try {
            await this.signInWithMicrosoftIdToken(auth, customToken);
        } catch (err) {
            this.setState({ page: startPage, loading: false, error: err.message, isSigningInToMicrosoft: false });
        }
    }

    signInWithMicrosoftIdToken = async (auth, customToken) => {
        logger.info("Signing in to Firebase with Microsoft auth credential");

        // Sign in with the custom token generated for the user
        const userCredential = await auth.signInWithCustomToken(customToken);

        // resume the loading animation, if any
        RestoreSplashScreen();

        // Ensure all experiments are initialized for the user
        await initializeAllExperiments().catch(err => logger.error(err, "Failed to initialize experiments"));

        // first sign-in results in account creation
        const isNew = await this.ensureRegistered("microsoft");
        if (isNew) {
            // Letting the consumers know that we're signing up a new user
            // we don't need to do that for email/password sign up because
            // it'll be set by the server upon email verification
            addQueryParam("signup", "true");

            this.trackCreateAccount(userCredential.user, true);
        } else {
            this.trackLogin(userCredential.user);
        }

        if (this._isUnmounted) {
            return;
        }

        this.setState({ loading: false, isSigningInToMicrosoft: false });
    }

    handleCreateAccount = auth => (async () => {
        const { email, password } = this.state;

        try {
            this.setState({ loading: true, error: undefined });

            const userCredential = await auth.createUserWithEmailAndPassword(email, password);

            // Ensure all experiments are initialized for the user
            await initializeAllExperiments().catch(err => logger.error(err, "Failed to initialize experiments"));

            await this.ensureRegistered("email");

            this.trackCreateAccount(userCredential.user, false);

            if (!this._isUnmounted) {
                this.setState({ loading: false });
            }
        } catch (err) {
            this.setState({ loading: false, error: err.message });
        }
    })

    trackCreateAccount = async (user, isGoogleSignup) => {
        // whatever happens next is part of the "signup action"
        Analytics.util.trackState({
            "is_signing_up": true
        });

        // Prepare extended props
        let extendedProps = {};
        if (this.props.reason === "team-invite") {
            extendedProps = {
                via_teaminvite_id: this.props.viaTeamInviteId,
                invited_by_user_id: this.props.invitedByUserId,
                invited_by_email: this.props.invitedByEmail,
                is_via_teaminvite: true
            };
        }

        Analytics.util.track({
            audit: true,
            category: "Auth",
            action: "signup",
            gtag: "conversion",
            props: {
                email: user.email,
                userId: user.uid,
                workspace_id: "all",
                ...extendedProps
            }
        });

        Analytics.util.track({
            audit: true,
            category: "SignupFlow",
            action: "NavForward",
            props: {
                step_number: 1,
                step_name: "Account Creation",
                object: "button",
                object_label: "Continue",
                action: "clicked",
                workspace_id: "all"
            }
        });

        if (isGoogleSignup) {
            Analytics.util.track({
                audit: true,
                category: "SignupFlow",
                action: "NavForward",
                props: {
                    step_number: 2,
                    step_name: "Verification",
                    verification_type: "google",
                    object: "button",
                    object_label: "Continue with Google",
                    action: "clicked",
                    workspace_id: "all"
                }
            });
        }
    }

    trackLogin = user => {
        Analytics.util.track({
            audit: true,
            category: "Auth",
            action: "login",
            props: {
                email: user.email,
                userId: user.uid,
                workspace_id: "all"
            }
        });
    }

    trackError = (email, err) => {
        const sanitizedErrorCode = err.code.replace("auth/", "");
        Api.authAudit.post({
            namespace: "analytics",
            email,
            props: {
                category: "Auth",
                action: sanitizedErrorCode
            }
        });
    }

    handleSwitchForm = ({ signIn }) => {
        this.setState({
            page: signIn ? "signIn" : "createAccount",
            error: undefined,
            emailError: undefined,
            passwordError: undefined
        });
    }

    handleClickForgot = () => {
        this.setState({ page: "forgotPassword" });
    }

    handleResetPassword = () => async () => {
        const { email } = this.state;
        try {
            await emailApi.sendPasswordResetEmail({ email });
            this.setState({ page: "resetDone" });
        } catch (err) {
            this.setState({ emailError: err.message });
        }
    }

    handleEmailChange = email => {
        this.setState({ email });
        return this.checkEmail(email);
    }

    handlePasswordChange = password => {
        this.setState({ password });
    }

    checkEmail = email => {
        if (this.ssoStatusCheckTimeout) {
            clearTimeout(this.ssoStatusCheckTimeout);
            this.ssoStatusCheckTimeout = null;
        }

        if (!isEmail(email)) {
            this.setState({ ssoState: "none" });
            return;
        }

        // parse the domain out of the email address
        const domain = email.split("@").pop();

        // check that the domain is includes an actual, listed TLD, this
        // prevents us from sending an excessive number of requests to our API
        const parsed = psl.parse(domain);
        if (parsed.sld && parsed.listed) {
            // debounce for common case of .co -> com
            this.ssoStatusCheckTimeout = setTimeout(async () => {
                try {
                    const { status } = await Api.ssoStatus.get({ email });
                    if (status === "strict") {
                        this.setState({ ssoState: status, page: "signIn" });
                        return;
                    }

                    this.setState({ ssoState: status });
                } catch (err) {
                    logger.error(err, "Api.ssoStatus.get() failed", { email });
                }
            }, 300);
            return;
        }

        this.setState({ ssoState: "none" });
    }

    handleFirstNameChange = value => {
        this.setState({
            firstName: value
        });
    }

    handleLastNameChange = value => {
        this.setState({
            lastName: value
        });
    }

    handleCompanyChange = value => {
        this.setState({
            company: value
        });
    }

    hero() {
        const { popup, reason, presentationName, presentationSharedBy, presentationThumbnailUrl } = this.props;
        if (popup) {
            return null;
        }
        switch (reason) {
            case "presentation-invite":
                return {
                    image: "presentation-invite",
                    thumbnailUrl: presentationThumbnailUrl,
                    caption: (
                        <span>
                            <b>{presentationName}</b>
                            <br />
                            shared by {presentationSharedBy}
                        </span>
                    )
                };
            default:
                return { image: reason };
        }
    }

    subheading() {
        const { popup, reason, page, teamName, provisioningSourceName } = this.props;
        if (popup) {
            return "";
        } else if (reason == "team-invite") {
            return <span>Join your team, <b>{teamName}</b>, and start creating beautiful presentations faster.</span>;
        } else if (page === "signIn" && reason == "presentation-invite") {
            return "Sign in to access this presentation";
        } else if (page === "createAccount" && reason == "presentation-invite") {
            return "Create a free account to access this presentation";
        } else if (reason == "provisioning") {
            return `${page === "signIn" ? "Sign in" : "Create an account"} to use Beautiful.ai in ${provisioningSourceName}`;
        } else if (page === "signIn" && reason === "zoom-app") {
            return "Sign in to use Beautiful.ai in your Zoom meeting";
        }
        return undefined;
    }

    render() {
        const {
            noVerify,
            popup,
            skipReload,
            backdropVisible,
            onClose,
            auth,
            currentUser,
            children
        } = this.props;
        const { isReady, loading } = this.state;

        if (!isReady) {
            return <Spinner />;
        }

        const isLoggedIn = currentUser && !loading;

        let content = children;
        if (isLoggedIn) {
            content = (
                <FirebaseSessionTracker user={currentUser}>
                    {this.renderPostAuth(currentUser, noVerify, (
                        popup && !skipReload
                            ? <ForceReload />
                            : content
                    ))}
                </FirebaseSessionTracker>
            );
        } else {
            RemoveSplashScreen();
            content = this.renderAuthContent();
        }

        if (popup) {
            content = (
                <Backdrop
                    invisible={!backdropVisible}
                    open={true}
                    onClick={e => {
                        window.top.postMessage("bai-auth-popup-close", window.location.origin);
                        onClose && onClose(e);
                    }}
                >
                    <div onClick={e => e.stopPropagation()}>
                        {content}
                    </div>
                </Backdrop>
            );
        }

        return (
            <GoogleRedirectHandler
                signInWithGoogleIdToken={idToken => this.handleGoogleIdToken(auth, idToken)}
                onError={error => this.setState({ error })}
            >
                <MicrosoftRedirectHandler
                    signInWithMicrosoftIdToken={customToken => this.handleMicrosoftIdToken(auth, customToken)}
                    onError={error => this.handleMicrosoftError(error)}
                >
                    {content}
                </MicrosoftRedirectHandler>
            </GoogleRedirectHandler>
        );
    }

    renderPostAuth(currentUser, noVerify, children) {
        RemoveSplashScreen();

        return (
            <Experiment id="must_verify_email">
                {isEnabled => {
                    if (isEnabled && !noVerify && !currentUser.emailVerified) {
                        return (
                            <VerifyEmail
                                email={currentUser.email}
                                noLogo={this.props.popup}
                            >
                                {children}
                            </VerifyEmail>
                        );
                    } else {
                        return children;
                    }
                }}
            </Experiment>
        );
    }

    renderAuthContent() {
        const { auth, popup, readonlyEmail, showOnlyGoogle, noToggle, reason } = this.props;
        const { error, info, email, emailError, password, passwordError, ssoState, loading, page } = this.state;

        let submitButtonLabel = null;

        if (page === "createAccount" && (reason === "team-invite" || reason === "presentation-invite")) {
            submitButtonLabel = "Create account";
        }

        switch (page) {
            case "signIn":
                return (
                    <SignIn
                        loading={loading}
                        error={error}
                        info={info}
                        emailError={emailError}
                        passwordError={passwordError}
                        defaultEmail={readonlyEmail}
                        email={email}
                        password={password}
                        showOnlyGoogle={showOnlyGoogle}
                        hero={this.hero()}
                        onClickSubmit={this.handleSignIn(auth)}
                        onClickGoogleButton={this.handleGoogle(auth)}
                        onClickMicrosoftButton={this.handleMicrosoft(auth)}
                        onClickForgot={this.handleClickForgot}
                        onClickSwitchLink={this.handleSwitchForm}
                        noToggle={!!readonlyEmail || noToggle || ssoState === "strict"}
                        ssoState={ssoState}
                        onEmailChange={this.handleEmailChange}
                        onPasswordChange={this.handlePasswordChange}
                        subheading={this.subheading()}
                        noLogo={popup}
                        hoistTerms={popup}
                        submitButtonLabel={submitButtonLabel}
                    />
                );
            case "createAccount":
                return (
                    <CreateAccount
                        loading={loading}
                        error={error}
                        info={info}
                        defaultEmail={readonlyEmail}
                        email={email}
                        password={password}
                        showOnlyGoogle={showOnlyGoogle}
                        withCompany={false}
                        onClickSubmit={this.handleCreateAccount(auth)}
                        onClickGoogleButton={this.handleGoogle(auth)}
                        onClickMicrosoftButton={this.handleMicrosoft(auth)}
                        onClickSwitchLink={this.handleSwitchForm}
                        hero={this.hero()}
                        noToggle={!!readonlyEmail || noToggle || ssoState === "strict"}
                        onEmailChange={this.handleEmailChange}
                        onPasswordChange={this.handlePasswordChange}
                        onFirstNameChange={this.handleFirstNameChange}
                        onLastNameChange={this.handleLastNameChange}
                        onCompanyChange={this.handleCompanyChange}
                        subheading={this.subheading()}
                        noLogo={popup}
                        hoistTerms={popup}
                        noTitle={!popup}
                        submitButtonLabel={submitButtonLabel}
                    />
                );
            case "forgotPassword":
                return (
                    <ResetPassword
                        email={email}
                        hero={this.hero()}
                        onClickReset={this.handleResetPassword(auth)}
                        emailError={emailError}
                        onEmailChange={email => this.setState({ email })}
                        noLogo={popup}
                    />
                );
            case "resetDone":
                return (
                    <ResetDone
                        hero={this.hero()}
                        noLogo={popup}
                    />
                );
            case "waitForGoogle":
                return (
                    <WaitForGoogle
                        hero={this.hero()}
                        noLogo={popup}
                    />
                );
            case "waitForMicrosoft":
                return (
                    <WaitForMicrosoft
                        hero={this.hero()}
                        noLogo={popup}
                    />
                );
            default:
                throw new Error();
        }
    }
}

// ugly hack to force popup page to reload inside main window
export function ForceReload() {
    const url = new URL(window.location.href);
    const search = new URLSearchParams(url.search);
    search.delete("popup");
    url.search = search.toString();
    window.top.location = url.href;
    return null;
}
